import { useState, useRef, useEffect, useCallback } from "react";
import {
  SendRegular, DeleteRegular, FlashRegular, GlobeRegular, BracesRegular, ArrowTrendingRegular, ChatRegular, ArrowSyncRegular, ClockRegular, ServerRegular,
} from "@fluentui/react-icons";
import { fluentIcon } from "../lib/fluentIcon";

const Send = fluentIcon(SendRegular);
const Trash2 = fluentIcon(DeleteRegular);
const Zap = fluentIcon(FlashRegular);
const Globe = fluentIcon(GlobeRegular);
const FileJson = fluentIcon(BracesRegular);
const TrendingUp = fluentIcon(ArrowTrendingRegular);
const MessageCircle = fluentIcon(ChatRegular);
const Loader2 = fluentIcon(ArrowSyncRegular);
const Clock = fluentIcon(ClockRegular);
const Server = fluentIcon(ServerRegular);

interface ModelEndpoint {
  id: string;
  label: string;
  url: string;
  description: string;
  params: string;
}

const MODELS: ModelEndpoint[] = [
  {
    id: "qwen-7b",
    label: "Qwen2.5-7B",
    url: "https://tomitra-inference-187545700403.asia-south1.run.app",
    description: "7B params, Q3_K_M — best quality, slower (~40-50s)",
    params: "7B",
  },
  {
    id: "qwen-3b",
    label: "Qwen2.5-3B",
    url: "https://tomitra-inference-3b-187545700403.asia-south1.run.app",
    description: "3B params, Q4_K_M — faster (~8s), good quality",
    params: "3B",
  },
  {
    id: "phi-3.5",
    label: "Phi-3.5-mini",
    url: "https://tomitra-inference-phi-187545700403.asia-south1.run.app",
    description: "3.8B params, Q4_K_M — best English quality, fast",
    params: "3.8B",
  },
];

interface Message {
  role: "user" | "assistant" | "error";
  content: string;
  meta?: string;
  model?: string;
}

const PRESETS: Record<string, { label: string; icon: any; system: string; json: boolean; temp: number; maxTokens: number; message: string }> = {
  summarize: {
    label: "Summarize",
    icon: Zap,
    system: `You are a news summarizer for Tomitra, an Indian news app.\nRules:\n- Write a 5-8 word headline (short_title)\n- 55-65 word summary\n- Pick category from: top, india, world, politics, business, technology, sports, entertainment, health, science, education, environment, startup, automobile, lifestyle\n- Return JSON with: short_title, summary, category, tags (up to 5)`,
    json: true,
    temp: 0.2,
    maxTokens: 512,
    message: "Summarize this article:\n\nIndia successfully launched its Chandrayaan-4 mission from Sriharikota on Tuesday. The spacecraft will attempt to bring back lunar soil samples, making India only the fourth country to achieve this feat. ISRO chief S Somanath said the mission represents a giant leap for India's space programme and will advance our understanding of the Moon's geology.",
  },
  chat_en: {
    label: "Chat (EN)",
    icon: MessageCircle,
    system: "You are Tomitra, an intelligent news companion. Answer in 30-45 words. Plain prose, no markdown. After answering, suggest 3 follow-up questions.",
    json: false,
    temp: 0.5,
    maxTokens: 300,
    message: "What is the significance of the Chandrayaan-4 mission for India?",
  },
  chat_hi: {
    label: "Chat (HI)",
    icon: Globe,
    system: "You are Tomitra, an intelligent news companion. Answer in 30-40 words. Write ONLY in Hindi DEVANAGARI script. No English, no Roman transliteration. Use simple conversational Hindi.",
    json: false,
    temp: 0.3,
    maxTokens: 300,
    message: "Chandrayaan-4 mission ka kya matlab hai?",
  },
  json_extract: {
    label: "JSON Extract",
    icon: FileJson,
    system: "You are a data extraction engine. Extract structured information and return JSON only.",
    json: true,
    temp: 0.1,
    maxTokens: 512,
    message: `Extract entities from: "PM Modi met President Biden in Washington to discuss trade and defense. The meeting took place at the White House on March 15."\n\nReturn JSON with: people (array), locations (array), date, topics (array)`,
  },
  trending: {
    label: "Trending",
    icon: TrendingUp,
    system: "You are a news trend analyst for India. Identify top 3 trending topics from headlines. Return JSON array with: id (slug), label (3 words max), emoji, context (12 words max), heat (1-5).",
    json: true,
    temp: 0.2,
    maxTokens: 512,
    message: "Headlines:\n1. Modi launches Digital India 2.0\n2. Sensex crosses 85000 for first time\n3. IPL: CSK beats MI in thriller\n4. AI startup raises $100M in Series B\n5. Chandrayaan-4 launch date confirmed\n6. Stock market hits all-time high\n7. India GDP growth at 7.2%\n8. New IT rules for social media",
  },
};

export default function LLMPlaygroundPage() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState("");
  const [system, setSystem] = useState("You are a helpful AI assistant. Be concise.");
  const [temp, setTemp] = useState(0.3);
  const [maxTokens, setMaxTokens] = useState(512);
  const [jsonMode, setJsonMode] = useState(false);
  const [streaming, setStreaming] = useState(true);
  const [loading, setLoading] = useState(false);
  const [selectedModel, setSelectedModel] = useState(MODELS[0].id);
  const [modelHealth, setModelHealth] = useState<Record<string, { status: string; model: string } | null>>({});

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const currentModel = MODELS.find(m => m.id === selectedModel) || MODELS[0];

  useEffect(() => {
    MODELS.forEach(m => {
      fetch(`${m.url}/health`).then(r => r.json())
        .then(data => setModelHealth(prev => ({ ...prev, [m.id]: data })))
        .catch(() => setModelHealth(prev => ({ ...prev, [m.id]: null })));
    });
  }, []);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const applyPreset = (key: string) => {
    const p = PRESETS[key];
    setSystem(p.system);
    setTemp(p.temp);
    setMaxTokens(p.maxTokens);
    setJsonMode(p.json);
    setInput(p.message);
    inputRef.current?.focus();
  };

  const sendMessage = useCallback(async () => {
    const msg = input.trim();
    if (!msg || loading) return;
    setInput("");

    const userMsg: Message = { role: "user", content: msg };
    setMessages(prev => [...prev, userMsg]);
    setLoading(true);

    const history = [...messages.filter(m => m.role !== "error"), userMsg];
    const apiMessages = [
      { role: "system", content: system },
      ...history.map(m => ({ role: m.role, content: m.content })),
    ];

    const body: any = {
      model: currentModel.label.toLowerCase().replace(/\./g, ""),
      messages: apiMessages,
      max_tokens: maxTokens,
      temperature: temp,
      stream: streaming,
    };
    if (jsonMode) body.response_format = { type: "json_object" };

    const t0 = Date.now();

    try {
      const res = await fetch(`${currentModel.url}/v1/chat/completions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!res.ok) throw new Error(await res.text());

      if (streaming) {
        // Add empty assistant message and stream into it
        setMessages(prev => [...prev, { role: "assistant", content: "", meta: "streaming..." }]);

        const reader = res.body!.getReader();
        const decoder = new TextDecoder();
        let full = "";

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          const lines = decoder.decode(value).split("\n");
          for (const line of lines) {
            if (!line.startsWith("data: ") || line === "data: [DONE]") continue;
            try {
              const d = JSON.parse(line.slice(6));
              const token = d.choices?.[0]?.delta?.content || "";
              full += token;
              setMessages(prev => {
                const copy = [...prev];
                copy[copy.length - 1] = { role: "assistant", content: full, meta: "streaming..." };
                return copy;
              });
            } catch {}
          }
        }

        const secs = ((Date.now() - t0) / 1000).toFixed(1);
        setMessages(prev => {
          const copy = [...prev];
          copy[copy.length - 1] = { role: "assistant", content: full, meta: `${secs}s | ${currentModel.label}`, model: currentModel.label };
          return copy;
        });
      } else {
        const data = await res.json();
        const text = data.choices[0].message.content;
        const secs = ((Date.now() - t0) / 1000).toFixed(1);
        const tokens = data.usage?.total_tokens || "?";
        setMessages(prev => [...prev, { role: "assistant", content: text, meta: `${secs}s | ${tokens} tokens | ${currentModel.label}`, model: currentModel.label }]);
      }
    } catch (e: any) {
      setMessages(prev => [...prev, { role: "error", content: e.message }]);
    }

    setLoading(false);
  }, [input, loading, messages, system, temp, maxTokens, jsonMode, streaming, currentModel]);

  const formatContent = (content: string) => {
    if (jsonMode || content.trim().startsWith("{") || content.trim().startsWith("[")) {
      try {
        return JSON.stringify(JSON.parse(content), null, 2);
      } catch {}
    }
    return content;
  };

  return (
    <div className="flex flex-col" style={{ height: "calc(100vh - 64px)" }}>
      {/* Header */}
      <div className="mb-4">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold">LLM Playground</h1>
        </div>
        <p className="text-sm text-slate-500 mt-1">Test and evaluate self-hosted models. Compare outputs, benchmark latency.</p>
      </div>

      {/* Model selector */}
      <div className="flex gap-2 mb-3">
        {MODELS.map(m => {
          const h = modelHealth[m.id];
          const isSelected = selectedModel === m.id;
          return (
            <button
              key={m.id}
              onClick={() => setSelectedModel(m.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl border text-sm transition-all ${
                isSelected
                  ? "bg-brand-blue/10 border-brand-blue/40 text-brand-blue"
                  : "bg-slate-900/60 border-slate-800 text-slate-400 hover:border-slate-700"
              }`}
            >
              <Server size={14} />
              <div className="text-left">
                <div className="font-medium">{m.label}</div>
                <div className="text-[10px] opacity-60">{m.description}</div>
              </div>
              <span className={`w-2 h-2 rounded-full ml-1 ${h ? "bg-emerald-400" : "bg-slate-600"}`} />
            </button>
          );
        })}
      </div>

      {/* Config bar */}
      <div className="bg-slate-900/60 border border-slate-800 rounded-xl p-4 mb-3">
        <div className="flex gap-4 flex-wrap items-end">
          <div className="flex-1 min-w-[300px]">
            <label className="text-xs text-slate-500 uppercase tracking-wider mb-1 block">System Prompt</label>
            <textarea
              value={system}
              onChange={e => setSystem(e.target.value)}
              rows={2}
              className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-xs text-slate-300 resize-none focus:border-brand-blue focus:outline-none"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500 uppercase tracking-wider mb-1 block">Temp</label>
            <input
              type="number"
              value={temp}
              onChange={e => setTemp(parseFloat(e.target.value))}
              min={0} max={2} step={0.1}
              className="w-16 bg-slate-950 border border-slate-800 rounded-lg px-2 py-2 text-xs text-slate-300 focus:border-brand-blue focus:outline-none"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500 uppercase tracking-wider mb-1 block">Max Tokens</label>
            <input
              type="number"
              value={maxTokens}
              onChange={e => setMaxTokens(parseInt(e.target.value))}
              min={1} max={4096} step={64}
              className="w-20 bg-slate-950 border border-slate-800 rounded-lg px-2 py-2 text-xs text-slate-300 focus:border-brand-blue focus:outline-none"
            />
          </div>
          <div>
            <label className="text-xs text-slate-500 uppercase tracking-wider mb-1 block">JSON</label>
            <button
              onClick={() => setJsonMode(!jsonMode)}
              className={`px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${
                jsonMode ? "bg-brand-blue/15 text-brand-blue border-brand-blue/30" : "bg-slate-950 text-slate-400 border-slate-800 hover:border-slate-700"
              }`}
            >
              {jsonMode ? "On" : "Off"}
            </button>
          </div>
          <div>
            <label className="text-xs text-slate-500 uppercase tracking-wider mb-1 block">Stream</label>
            <button
              onClick={() => setStreaming(!streaming)}
              className={`px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${
                streaming ? "bg-brand-blue/15 text-brand-blue border-brand-blue/30" : "bg-slate-950 text-slate-400 border-slate-800 hover:border-slate-700"
              }`}
            >
              {streaming ? "On" : "Off"}
            </button>
          </div>
        </div>
      </div>

      {/* Presets */}
      <div className="flex gap-2 mb-3 flex-wrap">
        {Object.entries(PRESETS).map(([key, p]) => (
          <button
            key={key}
            onClick={() => applyPreset(key)}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-900/60 border border-slate-800 rounded-lg text-xs text-slate-400 hover:text-brand-blue hover:border-brand-blue/30 transition-colors"
          >
            <p.icon size={12} />
            {p.label}
          </button>
        ))}
        <button
          onClick={() => { setMessages([]); setInput(""); }}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-900/60 border border-red-900/30 rounded-lg text-xs text-red-400/60 hover:text-red-400 hover:border-red-800 transition-colors ml-auto"
        >
          <Trash2 size={12} />
          Clear
        </button>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto bg-slate-950/50 border border-slate-800 rounded-xl p-4 space-y-3 mb-3">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-slate-600 gap-2">
            <MessageCircle size={32} />
            <p className="text-sm">Pick a preset or type a message to start testing</p>
          </div>
        )}
        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
            <div
              className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                msg.role === "user"
                  ? "bg-brand-blue text-white rounded-br-md"
                  : msg.role === "error"
                  ? "bg-red-950/50 border border-red-900/40 text-red-300 rounded-bl-md"
                  : "bg-slate-900/80 border border-slate-800 text-slate-200 rounded-bl-md"
              }`}
            >
              <pre className="whitespace-pre-wrap font-sans text-sm">{formatContent(msg.content)}</pre>
              {msg.meta && (
                <div className="flex items-center gap-2 mt-2 text-[10px] text-slate-500">
                  <Clock size={10} />
                  {msg.meta}
                </div>
              )}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="flex gap-2">
        <textarea
          ref={inputRef}
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
          placeholder="Type a message... (Shift+Enter for new line)"
          rows={2}
          className="flex-1 bg-slate-900/60 border border-slate-800 rounded-xl px-4 py-3 text-sm text-slate-200 resize-none focus:border-brand-blue focus:outline-none placeholder:text-slate-600"
        />
        <button
          onClick={sendMessage}
          disabled={loading || !input.trim()}
          className="px-5 bg-brand-blue hover:bg-blue-600 disabled:opacity-30 disabled:cursor-not-allowed text-white rounded-xl transition-colors flex items-center gap-2"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
        </button>
      </div>
    </div>
  );
}
